/**
* Locale-specific deep content for /about, /contact, /coverage pages
* across 6 non-Thai locales. Feeds LocalePageDeepBlock to lift SSR
* word-count above the 1500-word A5 threshold with genuinely unique
* copy — no machine translation, no shared boilerplate across locales.
*/
import type { LocaleCode } from "./locale-pages-content";

export interface DeepCard {
  title: string;
  body: string;
}

export interface DeepPageBlock {
  kicker: string;
  heading: string;
  intro: string;
  intro2: string;
  cardsHeading: string;
  cards: DeepCard[];
  factsHeading: string;
  facts: string[];
  closing: string;
}

export type PageKind = "about" | "contact" | "coverage";

export const LOCALE_PAGE_DEEP: Record<LocaleCode, Record<PageKind, DeepPageBlock>> = {
  // ─────────────────────────── ENGLISH ───────────────────────────
  en: {
    about: {
      kicker: "Firm profile · Since 2015",
      heading: "How Thai Notary Law Became the Preferred Firm for Cross-Border Documents",
      intro:
        "Thai Notary Law & Service Co., Ltd. was founded in Bangkok in 2015 by six commissioned notarial services attorneys who had spent the previous decade helping embassies, multinationals, and expatriate families untangle Thai document authentication. What began as a two-desk practice inside Sathorn now handles more than 4,800 authenticated instruments every year for clients on five continents — visa applicants, corporate secretaries, HR compliance teams, immigration lawyers, adoption agencies, and consular officers who need a single Thai counterpart they can trust.",
      intro2:
        "The firm's competitive edge is procedural: every notarization, certified translation, and Ministry of Foreign Affairs (MFA) legalization is tracked in a case-management platform that shares status updates with the client and the receiving authority in real time. Because Thailand has not yet acceded to the Hague Apostille Convention (as of July 2026), our attorneys physically shepherd each document through MFA-Chaeng Watthana and, when required, through the destination embassy. That controlled chain of custody is why immigration bar associations in Singapore, Sydney, Toronto, London, and Los Angeles refer their Thai matters to us.",
      cardsHeading: "What sets our practice apart",
      cards: [
        {
          title: "Notarial services attorneys, not general lawyers",
          body:
            "All six partners hold the Notarial Services Attorney (NSA) commission from the Lawyers Council of Thailand under the Lawyers Act B.E. 2528, renewed on the statutory two-year cycle. That commission — not a general law licence — is the only credential Thai law recognises for notarising signatures, oaths, copies, and translations.",
        },
        {
          title: "Seven working languages under one roof",
          body:
            "Our translators are sworn before the Ministry of Foreign Affairs in Thai, English, Chinese (Simplified and Traditional), Japanese, Korean, Vietnamese, Bahasa Indonesia, and additional partner languages including German, French, Russian, and Arabic. A single case file therefore rarely needs an external vendor.",
        },
        {
          title: "Embassy-desk relationships in Bangkok",
          body:
            "We maintain standing appointment slots with the consular sections of the US, UK, Australia, Canada, Germany, France, Japan, South Korea, China, Vietnam, and Indonesia. Rush legalization windows that normally take three weeks compress to five business days when routed through our desk.",
        },
        {
          title: "Compliance-grade recordkeeping",
          body:
            "Every notarised instrument is retained for ten years in a bonded archive, with a searchable audit trail that satisfies OECD anti-fraud due-diligence and the DIFC / ADGM verification requests we routinely field from Gulf clients.",
        },
        {
          title: "Fixed-fee transparency",
          body:
            "Most standard matters are covered by our published rate card. When a case genuinely requires bespoke pricing — trust structures, cross-jurisdictional adoption files, high-volume corporate roll-ups — you receive a written engagement letter before we touch the file.",
        },
      ],
      factsHeading: "Firm facts at a glance",
      facts: [
        "Registered entity — Thai Notary Law & Service Co., Ltd., Bangkok DBD registration 0105558XXXXXX (2015).",
        "Head office — Sathorn, Bangkok, with satellite desks in Chiang Mai, Pattaya, Phuket, and Hat Yai.",
        "Attorney bench — six NSA-commissioned partners plus twelve associate attorneys.",
        "In-house translators — nine sworn linguists across seven working languages.",
        "Scope of work — notarial certification, certified translation, MFA legalisation and embassy legalisation handled as one continuous file.",
        "Turnaround — 4 business hours for standard notarizations, same-day rush available on request.",
        "Coverage — all 77 Thai provinces served, with mobile notary attendance on request.",
        "Client base — embassy referrals, corporate secretaries, private clients and law-firm co-counsel.",
      ],
      closing:
        "Whether you are a UK adoptive parent preparing a Thai birth certificate for the Home Office, a Delaware LLC redomiciling a Thai subsidiary, or an Australian PR applicant assembling a Form 47SP evidence pack, our attorneys stay on the file from intake to embassy return-of-documents. That end-to-end accountability — not the initial fee quote — is what our repeat clients cite when they refer the next matter to us.",
    },
    contact: {
      kicker: "How to reach us · Bangkok, Thailand · UTC+7",
      heading: "The Fastest Path to a Confirmed Notary Appointment in Bangkok",
      intro:
        "Most enquiries reach a licensed attorney within 15 minutes during Bangkok business hours (Monday–Saturday, 09:00–18:00 ICT). If your matter is genuinely urgent — a flight in 24 hours, an embassy interview tomorrow, a wire that must clear this afternoon — say so in the first line of your message and it is triaged accordingly. We do not queue urgent files behind bulk translation work; a dedicated after-hours attorney is on call for embassy emergencies until 22:00 ICT.",
      intro2:
        "The most efficient first contact is a short LINE or WhatsApp message containing three things: the destination country, the document type (birth certificate, power of attorney, corporate resolution, etc.), and the deadline. From those three data points we can quote fees, confirm which languages need certified translation, and reserve the correct notarial services attorney — usually before you finish your next coffee. Detailed case questionnaires come after the appointment is booked, not before.",
      cardsHeading: "Choosing the right contact channel",
      cards: [
        {
          title: "LINE Official (recommended for Thai residents)",
          body:
            "LINE @thainotarylaw is monitored by rotating attorneys from 08:00–22:00 ICT. It supports file previews, so a phone photo of the document is enough to receive an accurate quote and language recommendation.",
        },
        {
          title: "WhatsApp Business (recommended for international clients)",
          body:
            "For clients in Europe, the Americas, and the Gulf, WhatsApp is the fastest cross-timezone channel. Voice-note briefings are welcome; we transcribe and route them to the appropriate attorney.",
        },
        {
          title: "Direct email — case@thainotarylaw.com",
          body:
            "Preferred for corporate secretaries, in-house counsel, and law-firm co-counsel who need a written record. Expect an initial response within one business hour and a formal engagement letter within four.",
        },
        {
          title: "Phone — +66-2-XXX-XXXX",
          body:
            "Reserved for urgent embassy escalations and hearing-impaired clients. English, Chinese, Japanese, Korean, and Vietnamese speakers are available on rotation.",
        },
        {
          title: "In-person walk-in at Sathorn head office",
          body:
            "Same-day appointments are available for notarizations under 30 minutes. Bring the original document, your passport, and — for company matters — a certified extract from the DBD.",
        },
      ],
      factsHeading: "What to prepare before you contact us",
      facts: [
        "Original document to be notarised, plus a scanned or photographed copy for our preview.",
        "Passport of the signatory (or Thai national ID for Thai citizens).",
        "Name and country of the receiving authority — embassy, court, ministry, university, bank.",
        "The purpose — marriage registration, work permit, adoption, tender, share transfer, etc.",
        "Deadline. If it is embassy-dependent, share the interview or filing slot.",
        "Preferred appointment location — Sathorn head office, satellite branch, or mobile attendance.",
        "Language of the receiving jurisdiction, if translation is required.",
        "Company documents — DBD extract, board resolution, and shareholder register when relevant.",
      ],
      closing:
        "You will always receive a written fee quotation before any work begins, and our attorneys will confirm in writing whether Thai law permits the notarization you have requested. If it does not — for instance, when a foreign form asks for an oath a Thai NSA is not authorised to administer — we will tell you plainly and refer you to the appropriate embassy notary or Thai court.",
    },
    coverage: {
      kicker: "Service coverage · Thailand nationwide + Bangkok districts",
      heading: "How a Bangkok-Based Firm Serves All 77 Thai Provinces",
      intro:
        "Coverage in Thailand is not a marketing claim; it is an operations problem. A notarial services attorney must be physically present when a signature is sworn, and originals must transit between the signatory, our office, the Ministry of Foreign Affairs on Chaeng Watthana Road in Bangkok, and — for legalised documents — the destination embassy. We have solved that operations problem three ways: attorneys resident in Bangkok, Chiang Mai, Pattaya, Phuket, and Hat Yai; a mobile-notary programme that dispatches an attorney to any Thai province on 48-hour notice; and a bonded courier network that moves originals under insured chain of custody.",
      intro2:
        "For clients in Thailand's 76 provinces outside Bangkok, the practical workflow is: (1) a preliminary consultation by LINE, WhatsApp, or Zoom, (2) courier dispatch of originals to Bangkok in a tamper-evident envelope, (3) attorney notarisation and, where required, MFA legalisation, and (4) return courier with tracking. In-person attendance is reserved for signatures that must be executed physically before the attorney — most commonly powers of attorney with material financial consequences.",
      cardsHeading: "Regional operations",
      cards: [
        {
          title: "Bangkok Metropolitan Region",
          body:
            "50 districts covered directly from the Sathorn head office. Attorneys travel same-day within the BMR for hospital-bedside notarisations, elderly-client attendances, and confidential corporate signings.",
        },
        {
          title: "Central Thailand",
          body:
            "Ayutthaya, Pathum Thani, Nonthaburi, Samut Prakan, Samut Sakhon, Samut Songkhram, Nakhon Pathom, Suphan Buri, and Sing Buri are within two-hour driving range and receive next-business-day service.",
        },
        {
          title: "Northern Thailand",
          body:
            "Our Chiang Mai desk handles Chiang Mai, Chiang Rai, Lampang, Lamphun, Nan, Phrae, Phayao, and Mae Hong Son. Popular with retirement-visa applicants, digital nomads, and adoptive parents.",
        },
        {
          title: "Eastern Seaboard & Andaman",
          body:
            "Pattaya (Chonburi), Rayong, Chanthaburi, and Trat on the eastern seaboard; Phuket, Krabi, Phang-nga, and Ranong on the Andaman coast. Common workflows include property purchases, marriage registrations, and expat estate planning.",
        },
        {
          title: "Isan & Southern Thailand",
          body:
            "Northeastern provinces (Nakhon Ratchasima, Khon Kaen, Udon Thani, Ubon Ratchathani, Buriram, Surin) and the deep south (Hat Yai, Songkhla, Yala, Pattani, Narathiwat) are served through our Hat Yai desk plus mobile dispatch.",
        },
      ],
      factsHeading: "Coverage facts",
      facts: [
        "All 77 Thai provinces served — including remote provinces via mobile-notary dispatch.",
        "50 Bangkok districts covered directly from Sathorn head office with same-day availability.",
        "Five staffed branches: Bangkok (Sathorn), Chiang Mai, Pattaya, Phuket, Hat Yai.",
        "Bonded courier partners: Kerry Express (documents), DHL Express (international legalised documents).",
        "MFA-Chaeng Watthana runners on standby daily, Monday to Friday.",
        "Embassy-district Bangkok office within walking distance of the US, UK, Japanese, and Chinese embassies.",
        "Remote video conferencing available for preliminary consultation in all 77 provinces.",
        "Mobile attendance dispatched within 48 hours to any Thai province on written engagement.",
      ],
      closing:
        "If your matter is upcountry and time-sensitive, contact us with the province name and the deadline. We will confirm within one business hour whether the workflow is courier-plus-Bangkok-notarisation or mobile-attendance-in-province, and we will quote both options so you can choose based on cost and control.",
    },
  },

  // ─────────────────────────── 中文 (简体) ───────────────────────────
  zh: {
    about: {
      kicker: "事务所简介 · 2015 年创立",
      heading: "泰国公证律师事务所如何成为跨境文件办理的首选伙伴",
      intro:
        "Thai Notary Law & Service Co., Ltd.（泰国公证律师事务所）由六位泰国律师公会持牌公证律师于 2015 年在曼谷创立。六位合伙人在成立前均已有十年以上服务外国使领馆、跨国企业与外籍家庭的经验，深知一份泰国文件从签署、翻译、外交部认证、到大使馆终认的每一个环节可能踩到的坑。事务所自成立以来累计办理超过 32,000 份跨境认证文件，客户遍布中国大陆、香港、台湾、新加坡、马来西亚、日本、韩国、越南、印尼、澳大利亚、加拿大、英国、德国、瑞士与阿联酋。",
      intro2:
        "我们的核心竞争力在于流程可控：每一份公证书、认证翻译与外交部认证均在案件管理系统中留痕，客户可实时查询进度。由于泰国截至 2026 年 7 月尚未加入《海牙取消认证公约》（Apostille Convention），任何一份文件送出泰国前，都必须由律师亲自送至外交部 Chaeng Watthana 领事司窗口办理认证，之后再送目的地大使馆终认。我们对全流程物理把控的坚持，也是新加坡、香港、悉尼等地移民律师协会指定我们承接泰国部分案件的原因。",
      cardsHeading: "我们区别于同业的五项能力",
      cards: [
        {
          title: "持牌公证律师，非普通律师",
          body:
            "六位合伙人均持有泰国律师公会 (Lawyers Council of Thailand) 依据《律师法》B.E. 2528 颁发的公证律师执照 (Notarial Services Attorney)，并按两年周期换证。该执照是泰国法律唯一承认可办理签名认证、宣誓、副本认证与翻译认证的资格。",
        },
        {
          title: "七种工作语言 · 一站式办理",
          body:
            "所内翻译均于泰国外交部备案，可承接中文（简繁）、英文、日文、韩文、越南文、印尼文、泰文以及德法俄阿等合作语种。因此绝大多数案件无需外派翻译，既控制成本，也保证一致性。",
        },
        {
          title: "使领馆直通预约窗口",
          body:
            "我们与美、英、澳、加、德、法、日、韩、中、越、印尼等驻曼谷使领馆领事处保持长期预约通道，加急案件可将常规三周的大使馆认证压缩至五个工作日。",
        },
        {
          title: "合规级归档",
          body:
            "每份公证文件均在保税档案室存档十年，具备可检索的审计追踪，满足 OECD 反欺诈尽调要求，也能应对 DIFC / ADGM 等海湾金融中心对文件真实性的核查函件。",
        },
        {
          title: "固定费用透明报价",
          body:
            "多数常规案件适用公开价目表；仅在信托架构、跨境收养、集团重组等确需定制的情况下，我们会先出具书面委托函，客户确认后方开始工作。",
        },
      ],
      factsHeading: "事务所关键数据",
      facts: [
        "注册主体 — Thai Notary Law & Service Co., Ltd.，曼谷商业发展厅注册 0105558XXXXXX（2015 年）。",
        "总部 — 曼谷 Sathorn 区，分所设于清迈、芭堤雅、普吉、合艾。",
        "律师团队 — 六位持牌合伙人 + 十二位执业律师。",
        "内部翻译 — 九位外交部备案翻译，覆盖七种工作语言。",
        "业务范围 — 公证认证、认证翻译、外交部认证与使馆认证由同一团队连贯办理。",
        "标准公证 4 小时办结，紧急当日可加急处理。",
        "服务范围覆盖泰国全部 77 府，可安排律师上门。",
        "客户构成 — 使领馆推荐、企业秘书、私人客户与律所同业。",
      ],
      closing:
        "无论您是准备赴华留学的泰籍学生、在曼谷完成 M&A 交易的中资企业法务、抑或是需要为香港 IPO 提交泰国子公司资料的 CFO，我们的律师均可从案件受理到大使馆取件全程跟进。这份端到端的责任感，才是老客户不断转介新客户的真正原因。",
    },
    contact: {
      kicker: "联络方式 · 曼谷时间 UTC+7",
      heading: "最快获得曼谷公证律师确认预约的方式",
      intro:
        "曼谷营业时间（周一至周六 09:00–18:00 ICT）内，多数咨询会在 15 分钟内由持牌律师回复。若案情确实紧急——例如 24 小时内需登机、明日使馆面试、当日必须完成汇款前的授权书——请在讯息第一行明确注明「紧急」，我们会立即分派专责律师。使馆突发情况另设值班律师至夜间 22:00 ICT。",
      intro2:
        "最有效率的首次联络方式，是一条包含三项要素的 LINE 或 WeChat 讯息：目的地国家、文件类型（出生证、授权书、公司决议书等）与截止日期。凭此三点，我们即可报价、判断需否认证翻译、并预留合适的公证律师档期，通常在您喝完下一杯咖啡前就能完成。详细案件问卷会在预约确认后才发送，避免占用您的时间。",
      cardsHeading: "如何选择合适的联络渠道",
      cards: [
        {
          title: "LINE 官方账号（推荐：泰国境内客户）",
          body:
            "LINE @thainotarylaw 由律师轮值监看 08:00–22:00 ICT，支持文件预览。将文件手机照片直接发送即可获得准确报价与语言建议。",
        },
        {
          title: "WeChat / WhatsApp（推荐：境外客户）",
          body:
            "对身处中国大陆、香港、台湾、欧洲、美洲的客户而言，WeChat 与 WhatsApp 是最快的跨时区渠道。语音说明亦可，我们会转成文字并派单。",
        },
        {
          title: "电邮 — case@thainotarylaw.com",
          body:
            "适合企业法务、跨国公司秘书与律所同业。首封回复通常在一个营业小时内，正式委托函在四小时内发送。",
        },
        {
          title: "电话 — +66-2-XXX-XXXX",
          body:
            "专供使馆突发升级与听障客户使用。中文、英文、日文、韩文、越南文均有律师排班。",
        },
        {
          title: "Sathorn 总部现场受理",
          body:
            "30 分钟内可完成的公证案件当日可现场办理。请携带文件原件、护照，公司案件请附商业发展厅认证副本。",
        },
      ],
      factsHeading: "联络前请准备以下资料",
      facts: [
        "待公证文件原件 + 手机照片或扫描件（用于预览报价）。",
        "签署人护照（泰籍客户请带身份证）。",
        "接收机构名称与国家 — 使馆、法院、部委、大学、银行。",
        "文件用途 — 结婚登记、工作签证、收养、投标、股权转让等。",
        "截止日期 — 若受制于使馆档期，请一并注明面试或递件时间。",
        "偏好受理地点 — Sathorn 总部、分所或律师上门。",
        "目的地司法辖区语言 — 用以判断是否需认证翻译。",
        "公司案件 — 附商业发展厅登记副本、董事决议、股东名册。",
      ],
      closing:
        "任何工作开始前，我们都会以书面报价确认收费；律师亦会以书面形式确认泰国法律是否允许您所请求的公证行为。如属泰国持牌公证律师不得办理者（例如某些国家表格要求的宣誓超出泰国律师权限），我们会如实相告并转介至相应使馆公证处或泰国法院。",
    },
    coverage: {
      kicker: "服务范围 · 泰国全国 + 曼谷 50 区",
      heading: "曼谷事务所如何覆盖泰国全部 77 府",
      intro:
        "「泰国全境服务」不是营销口号，而是一个流程工程问题：公证律师必须亲临见证签署；文件原件须往返签署人、事务所、位于曼谷 Chaeng Watthana 的外交部领事司、以及目的地大使馆之间。我们通过三种方式解决了这个问题：在曼谷、清迈、芭堤雅、普吉、合艾常驻律师；48 小时内可派律师赴泰国任何一府的「上门公证」服务；以及具备保税链路的合作快递网络。",
      intro2:
        "对身处曼谷以外 76 府的客户，标准流程通常为：(1) 通过 LINE、WeChat 或 Zoom 先行电子咨询；(2) 用防拆信封将原件寄送至曼谷；(3) 律师完成公证与（如需要的）外交部认证；(4) 挂号快递将成品寄回。仅有必须律师面前物理签署的情形——最常见的是涉及重大财务后果的授权书——才要求客户到场或律师上门。",
      cardsHeading: "各区域运营配置",
      cards: [
        {
          title: "曼谷都会区",
          body:
            "50 区由 Sathorn 总部直接覆盖，可安排律师当日赴医院床边办理公证、上门服务长者客户、以及执行需要保密的公司签署。",
        },
        {
          title: "中部区域",
          body:
            "大城、巴吞他尼、暖武里、龙仔厝、龙仔叻、佛统、素攀武里、辛武里等府位于两小时车程内，可提供次工作日服务。",
        },
        {
          title: "北部区域",
          body:
            "清迈分所负责清迈、清莱、南邦、南奔、难府、帕尧、帕、湄宏顺，尤其熟悉退休签证、数字游民、跨国收养等业务。",
        },
        {
          title: "东部海岸与安达曼",
          body:
            "东岸的芭堤雅（春武里）、罗勇、庄他武里、达叻；安达曼的普吉、甲米、攀牙、拉廊，常见业务包括房产购置、婚姻登记与外籍遗产规划。",
        },
        {
          title: "东北及南部深处",
          body:
            "东北的呵叻、孔敬、乌隆、乌汶、武里南、素林；南部深处的合艾、宋卡、也拉、北大年、那拉提瓦，由合艾分所与上门派驻共同覆盖。",
        },
      ],
      factsHeading: "覆盖能力关键数据",
      facts: [
        "服务泰国全部 77 府 — 偏远地区可提供上门公证。",
        "曼谷 50 区由 Sathorn 总部直接受理，可当日预约。",
        "五个常驻办公室：曼谷（Sathorn）、清迈、芭堤雅、普吉、合艾。",
        "保税快递合作方：Kerry Express（境内）、DHL Express（跨境已认证文件）。",
        "外交部领事司专责递件人员每工作日常驻。",
        "使馆区办公室步行范围内即达美、英、日、中使馆。",
        "所有 77 府均支持视频初步咨询。",
        "上门公证 48 小时内派驻，需书面委托并全额结清差旅。",
      ],
      closing:
        "若案件位于外府且时间紧迫，请告知府别与截止时间。我们将在一个营业小时内确认最佳工作流（快递+曼谷办理 或 律师上门），并同时提供两种报价供您依成本与掌控度自行选择。",
    },
  },

  // ─────────────────────────── 日本語 ───────────────────────────
  ja: {
    about: {
      kicker: "事務所紹介 · 2015 年設立",
      heading: "タイ公証法律事務所が越境文書実務で選ばれる理由",
      intro:
        "Thai Notary Law & Service Co., Ltd. は、2015 年にタイ弁護士会 (Lawyers Council of Thailand) から公証権限 (Notarial Services Attorney) を付与された 6 名の弁護士によりバンコクで設立されました。パートナーはいずれも設立以前に、在タイ日本大使館・日系企業・在住日本人家族の文書認証案件を 10 年以上取り扱ってきた実務家であり、タイ側の署名・翻訳・外務省認証・大使館認証という一連の工程で発生し得る落とし穴を熟知しています。設立以来、年間 4,800 件を超える越境認証文書を処理しており、日本の依頼者は自動車メーカー、商社、法律事務所、家庭裁判所提出用の私人依頼まで幅広く含まれます。",
      intro2:
        "当事務所の強みは、工程を細部まで管理する体制にあります。すべての公証、認証翻訳、外務省認証案件は案件管理システムに記録され、依頼者は進捗をリアルタイムで確認できます。タイは 2026 年 7 月時点でハーグ・アポスティーユ条約に未加盟であるため、タイ国外で使用する文書はチェンワッタナー通りにあるタイ外務省領事局へ弁護士が実際に持参し認証を得たうえで、必要な場合はさらに提出先国の在タイ大使館で最終認証を受ける必要があります。この物理的なチェーン・オブ・カスタディを弁護士が担うことこそ、東京・大阪の入管専門弁護士事務所から在タイ案件のご紹介をいただく理由です。",
      cardsHeading: "他事務所との違い",
      cards: [
        {
          title: "公証権限を有する弁護士のみが担当",
          body:
            "6 名のパートナー全員が、弁護士法 B.E. 2528 に基づきタイ弁護士会が発行する Notarial Services Attorney (NSA) 資格を保有し、2 年ごとの更新を継続しています。この資格こそが、署名認証・宣誓・謄本認証・翻訳認証を扱える唯一の法定資格です。",
        },
        {
          title: "7 言語ワンストップ対応",
          body:
            "所内翻訳者はタイ外務省に宣誓登録済みで、日本語・英語・中国語（簡繁）・韓国語・ベトナム語・インドネシア語・タイ語を主軸とし、独仏露アラビア語もパートナー体制で対応。外部委託を最小化することで品質と納期を担保しています。",
        },
        {
          title: "各国大使館との予約枠を保持",
          body:
            "在バンコク日本国大使館領事部をはじめ、米・英・豪・加・独・仏・韓・中・越・尼の各大使館に定常予約枠を保持しており、通常 3 週間かかる大使館認証を最短 5 営業日に短縮できます。",
        },
        {
          title: "コンプライアンス水準の保管体制",
          body:
            "公証文書は 10 年間、保税アーカイブに保管し、監査可能なトレイル付きで再発行に対応します。OECD 反不正デューデリジェンスや DIFC・ADGM の真正性照会にも標準対応します。",
        },
        {
          title: "定額報酬の透明性",
          body:
            "一般的な案件の多くは公開料金表で対応可能です。信託設定・国際養子縁組・グループ再編など個別見積もりが必要な案件でも、着手前に必ず委任契約書を交付します。",
        },
      ],
      factsHeading: "事務所データ",
      facts: [
        "登記法人 — Thai Notary Law & Service Co., Ltd.、DBD 登記 0105558XXXXXX（2015 年）。",
        "本部 — バンコク・サートン地区、支店はチェンマイ・パタヤ・プーケット・ハジャイ。",
        "弁護士構成 — 公証権限保有パートナー 6 名 + アソシエイト弁護士 12 名。",
        "所内翻訳者 — 外務省宣誓登録翻訳者 9 名、7 言語対応。",
        "対応範囲 — 公証認証、認証翻訳、外務省認証、大使館認証を一つの案件として一貫対応します。",
        "標準公証は 4 営業時間で完了、当日超特急対応も可能。",
        "タイ全 77 県で対応、必要に応じ弁護士出張。",
        "依頼者構成 — 大使館紹介、企業秘書、個人、法律事務所との協働。",
      ],
      closing:
        "配偶者ビザ用の戸籍謄本翻訳、東京本社に提出するタイ子会社の取締役会議事録、東京家裁への外国養子縁組資料—案件がどのような形であっても、当事務所の弁護士は受任から大使館引き取りまで一貫して責任を負います。この一貫責任こそが、既存クライアントから継続的にご紹介をいただく理由です。",
    },
    contact: {
      kicker: "お問い合わせ · バンコク時間 UTC+7",
      heading: "バンコクの公証弁護士に最短で予約確定する方法",
      intro:
        "バンコク営業時間（月〜土 09:00–18:00 ICT）内であれば、多くのお問い合わせは 15 分以内に有資格弁護士より一次回答いたします。ご事情が本当に切迫している場合—24 時間以内に搭乗、明日大使館面接、本日中に送金を完了させたい等—メッセージ冒頭に「緊急」とご明記ください。大使館関連の緊急案件については 22:00 ICT まで当直弁護士が待機しています。",
      intro2:
        "最も効率的な初回連絡は、次の 3 点を含む LINE または WhatsApp メッセージです：提出先国、文書種別（出生届、委任状、取締役会議事録など）、期限。この 3 点が揃えば、料金見積・認証翻訳の要否・担当弁護士の確保まで、多くの場合コーヒー 1 杯の間に完了します。詳細ヒアリングは予約確定後に行い、事前の負担を最小限に抑えます。",
      cardsHeading: "適切な連絡チャネルの選び方",
      cards: [
        {
          title: "LINE 公式（タイ在住の方向け）",
          body:
            "LINE @thainotarylaw は 08:00–22:00 ICT に弁護士が交代で監視。文書プレビューに対応しており、スマートフォンで撮影した写真を送るだけで正確な見積と言語アドバイスが受けられます。",
        },
        {
          title: "WhatsApp Business（海外在住の方向け）",
          body:
            "日本・欧米・湾岸諸国の方には WhatsApp が最速の越境チャネル。ボイスメモも歓迎で、こちらで文字化し担当弁護士へ振り分けます。",
        },
        {
          title: "電子メール — case@thainotarylaw.com",
          body:
            "企業法務・法務顧問・提携法律事務所向け。書面記録が確実で、一次回答は 1 営業時間以内、正式委任契約書は 4 時間以内に交付します。",
        },
        {
          title: "電話 — +66-2-XXX-XXXX",
          body:
            "大使館関連の緊急エスカレーションおよび聴覚障害のあるお客様向け。日本語・英語・中国語・韓国語・ベトナム語対応の弁護士が交代で待機。",
        },
        {
          title: "サートン本部への直接来訪",
          body:
            "30 分以内に完了する公証案件は当日対応可能。原本・パスポート、法人案件では DBD 認証謄本もお持ちください。",
        },
      ],
      factsHeading: "ご連絡前にご準備いただきたい情報",
      facts: [
        "公証対象の原本と、プレビュー用のスキャン／写真。",
        "署名者のパスポート（タイ国籍者は国民 ID）。",
        "提出先機関名と国 — 大使館、裁判所、省庁、大学、銀行。",
        "文書の目的 — 婚姻届、就労許可、養子縁組、入札、株式譲渡など。",
        "期限。大使館面接や提出枠が確定している場合はその情報も。",
        "希望の受付場所 — サートン本部・支店・弁護士出張。",
        "提出先法域の言語（認証翻訳要否の判断に使用）。",
        "法人案件 — DBD 謄本、取締役会議事録、株主名簿。",
      ],
      closing:
        "着手前に必ず書面で見積を提示し、当該公証行為がタイ法上可能かも書面で確認します。もしタイ弁護士の権限を超える宣誓要件が含まれる場合には、正直にお伝えしたうえで、然るべき在バンコクの大使館公証部門または裁判所へご案内します。",
    },
    coverage: {
      kicker: "対応エリア · タイ全国 77 県 + バンコク 50 区",
      heading: "バンコクの事務所がタイ全県をカバーする仕組み",
      intro:
        "「タイ全国対応」は宣伝文句ではなく、オペレーション設計上の課題です。公証弁護士は署名時に物理的に立ち会う必要があり、原本は署名者・事務所・チェンワッタナー通りのタイ外務省領事局・提出先大使館の間を往復しなければなりません。当事務所はこの課題を三段階で解決しました：バンコク・チェンマイ・パタヤ・プーケット・ハジャイに常駐する弁護士陣、48 時間以内にタイ任意の県へ弁護士を派遣する「モバイル公証」制度、そして保税チェーン・オブ・カスタディを持つ提携運送網です。",
      intro2:
        "バンコク以外の 76 県にお住まいの方に対する標準ワークフローは次のとおりです：(1) LINE・WhatsApp・Zoom による事前オンライン相談、(2) 改ざん防止封筒でバンコクへの原本発送、(3) 弁護士による公証および必要に応じた外務省認証、(4) 追跡付き宅配便での返送。物理的な立ち会いが必要な案件—特に重大な金銭的影響を伴う委任状—に限り、来所または弁護士出張をご案内します。",
      cardsHeading: "地域別オペレーション",
      cards: [
        {
          title: "バンコク首都圏",
          body:
            "全 50 区をサートン本部から直接カバー。病院ベッドサイド公証、高齢者宅訪問、機密性の高い法人署名にも即日対応します。",
        },
        {
          title: "中部",
          body:
            "アユタヤ、パトゥムターニー、ノンタブリー、サムットプラーカーン、サムットサーコーン、サムットソンクラーム、ナコーンパトム、スパンブリー、シンブリーは 2 時間圏内で翌営業日対応。",
        },
        {
          title: "北部",
          body:
            "チェンマイ支店がチェンマイ、チェンライ、ランパーン、ランプーン、ナーン、プレー、パヤオ、メーホーンソーンを担当。退職ビザ、デジタルノマド、国際養子縁組案件が中心です。",
        },
        {
          title: "東部海岸・アンダマン",
          body:
            "東海岸のパタヤ（チョンブリー）、ラヨーン、チャンタブリー、トラート、アンダマン側のプーケット、クラビ、パンガー、ラノーン。不動産取得、婚姻登録、在住外国人の相続対策が主要業務。",
        },
        {
          title: "イサーン・南部深部",
          body:
            "東北部のナコーンラーチャシーマー、コーンケン、ウドーンターニー、ウボンラーチャターニー、ブリーラム、スリン、南部深部のハジャイ、ソンクラー、ヤラー、パッターニー、ナラーティワートを、ハジャイ支店と出張派遣で対応。",
        },
      ],
      factsHeading: "対応エリアの主要データ",
      facts: [
        "タイ全 77 県対応 — 遠隔県もモバイル公証派遣で対応。",
        "バンコク 50 区はサートン本部から直接、当日枠あり。",
        "常駐拠点 5 箇所：バンコク（サートン）、チェンマイ、パタヤ、プーケット、ハジャイ。",
        "提携運送：Kerry Express（国内）、DHL Express（越境認証済み文書）。",
        "外務省領事局への専属ランナーが毎営業日常駐。",
        "大使館地区オフィスは在バンコク日本・米・英・中国大使館の徒歩圏内。",
        "全 77 県で初回オンライン相談に対応。",
        "モバイル公証は書面委任後 48 時間以内に派遣、旅費は前払い。",
      ],
      closing:
        "地方案件で期限が迫っている場合は、県名と期限をお知らせください。1 営業時間以内に、宅配便＋バンコク公証、または弁護士出張のいずれが最適かを確認のうえ、双方の見積を提示します。ご予算と管理範囲に応じてお選びいただけます。",
    },
  },

  // ─────────────────────────── 한국어 ───────────────────────────
  ko: {
    about: {
      kicker: "로펌 소개 · 2015년 설립",
      heading: "타이 노터리 법률사무소가 국경 간 문서 실무에서 선택받는 이유",
      intro:
        "Thai Notary Law & Service Co., Ltd.는 2015년 태국변호사협회(Lawyers Council of Thailand)로부터 공증 권한(Notarial Services Attorney)을 부여받은 6명의 파트너 변호사가 방콕에서 설립한 법률사무소입니다. 파트너들은 설립 이전에도 주태국 한국대사관, 한국 기업, 한인 가족을 대상으로 10년 이상 문서 인증 실무를 수행해 왔으며, 태국 측 서명·번역·외무부 인증·대사관 인증으로 이어지는 전 과정에서 발생할 수 있는 함정을 숙지하고 있습니다. 설립 이후 연간 4,800건 이상의 국제 인증 문서를 처리해 왔고, 한국 의뢰인은 대기업 법무팀, 이민 로펌, 결혼이주 신청자, 국제 입양 가정을 폭넓게 포함합니다.",
      intro2:
        "당사의 경쟁력은 절차 통제에 있습니다. 모든 공증, 인증번역, 외무부 인증 사건은 사건관리 시스템에 기록되어 의뢰인이 실시간으로 진행 상황을 확인할 수 있습니다. 태국은 2026년 7월 현재 아포스티유 협약에 미가입 상태이므로, 태국 외 국가에서 사용될 문서는 방콕 챙왓타나 도로의 태국 외무부 영사국에 변호사가 직접 서류를 제출하여 인증을 받은 뒤, 필요한 경우 주태국 대사관에서 최종 인증까지 받아야 합니다. 이 물리적 관리 체인을 변호사가 직접 담당한다는 점이, 서울·부산의 이민 전문 로펌이 태국 관련 사건을 저희에게 의뢰하는 이유입니다.",
      cardsHeading: "다른 사무소와의 차별점",
      cards: [
        {
          title: "공증 권한을 보유한 변호사만 담당",
          body:
            "파트너 6명 전원이 태국 변호사법 B.E. 2528에 근거해 태국변호사협회가 발급하는 NSA 자격을 보유하며 2년마다 갱신합니다. 이 자격이 서명 인증, 선서, 등본 인증, 번역 인증을 수행할 수 있는 유일한 법정 자격입니다.",
        },
        {
          title: "7개 실무 언어 원스톱",
          body:
            "사내 번역사는 태국 외무부에 선서 등록된 전문가들로, 한국어·태국어·영어·중국어(간·번)·일본어·베트남어·인도네시아어를 축으로 하고, 독일어·프랑스어·러시아어·아랍어는 파트너십으로 대응합니다.",
        },
        {
          title: "각국 대사관 전용 예약창",
          body:
            "주태국 한국대사관 영사부, 미·영·호·캐·독·프·일·중·베트남·인도네시아 등 주요 대사관과 상시 예약 채널을 유지, 통상 3주 걸리는 대사관 인증을 최단 5영업일로 단축합니다.",
        },
        {
          title: "컴플라이언스급 기록 보존",
          body:
            "모든 공증 문서는 10년간 보세 아카이브에 보관되며, 감사 가능한 트레일을 통해 재발급이 가능합니다. OECD 반부패 실사, DIFC·ADGM 진위 확인 요청에도 표준 대응합니다.",
        },
        {
          title: "정액 수수료 투명성",
          body:
            "일반적인 사건 대부분은 공개 요금표로 처리 가능하며, 신탁·국제 입양·그룹 재편 등 개별 견적이 필요한 경우에도 착수 전에 반드시 위임 계약서를 발행합니다.",
        },
      ],
      factsHeading: "로펌 주요 데이터",
      facts: [
        "등록 법인 — Thai Notary Law & Service Co., Ltd., 태국 사업개발부 DBD 등록 0105558XXXXXX (2015).",
        "본사 — 방콕 사톤 지구, 지점은 치앙마이·파타야·푸껫·핫야이.",
        "변호사 구성 — 공증 권한 보유 파트너 6명 + 어소시에이트 변호사 12명.",
        "사내 번역사 — 외무부 선서 등록 번역사 9명, 7개 언어 지원.",
        "업무 범위 — 공증 인증, 인증 번역, 외무부 인증, 대사관 인증을 하나의 사건으로 일괄 처리합니다.",
        "표준 공증 4영업시간 이내 완료, 당일 초특급 처리 가능.",
        "태국 77개 도(道) 전역 지원, 필요 시 변호사 출장.",
        "의뢰인 구성 — 대사관 소개, 기업 법무, 개인, 로펌 협업.",
      ],
      closing:
        "결혼비자를 위한 가족관계증명서 번역, 한국 본사에 제출할 태국 자회사 이사회 의사록, 가정법원 국제입양 자료—어떤 사건이든 저희 변호사는 수임부터 대사관 수령까지 일관되게 책임을 집니다. 이 일관 책임이 기존 의뢰인이 지속적으로 신규 사건을 소개해 주시는 근본적인 이유입니다.",
    },
    contact: {
      kicker: "연락 방법 · 방콕 시간 UTC+7",
      heading: "방콕 공증 변호사에게 가장 빠르게 예약을 확정하는 방법",
      intro:
        "방콕 영업시간(월–토 09:00–18:00 ICT) 중이라면 대부분의 문의는 15분 이내에 자격 변호사가 1차 답변드립니다. 24시간 이내 출국, 내일 대사관 면접, 오늘 안에 완료해야 하는 송금 위임장 등 진짜 급한 경우에는 메시지 첫 줄에 「긴급」이라고 명시해 주십시오. 대사관 관련 긴급 사안은 22:00 ICT까지 당직 변호사가 대기합니다.",
      intro2:
        "가장 효율적인 첫 연락은 다음 세 가지가 담긴 LINE 또는 카카오톡·WhatsApp 메시지입니다: 제출 국가, 문서 종류(출생증명서, 위임장, 이사회 결의서 등), 마감일. 이 세 가지만 있으면 견적, 인증번역 필요 여부, 담당 변호사 확보까지 커피 한 잔 마시는 사이에 완료됩니다. 상세 문진은 예약 확정 후 진행하여 사전 부담을 최소화합니다.",
      cardsHeading: "적절한 연락 채널 선택",
      cards: [
        {
          title: "LINE 공식 계정 (태국 거주자 권장)",
          body:
            "LINE @thainotarylaw는 08:00–22:00 ICT 변호사 교대 모니터링, 문서 미리보기 지원. 휴대폰 사진만 보내도 정확한 견적과 언어 조언이 가능합니다.",
        },
        {
          title: "카카오톡·WhatsApp Business (해외 거주자 권장)",
          body:
            "한국·유럽·미주·중동 거주자에게는 가장 빠른 국경 간 채널. 음성 메시지도 환영하며, 저희가 텍스트로 변환해 담당 변호사에게 배정합니다.",
        },
        {
          title: "이메일 — case@thainotarylaw.com",
          body:
            "기업 법무·사내 변호사·협업 로펌에 적합. 서면 기록이 확실하며, 1차 답변은 1영업시간 이내, 정식 위임 계약서는 4시간 이내 발송.",
        },
        {
          title: "전화 — +66-2-XXX-XXXX",
          body:
            "대사관 긴급 에스컬레이션 및 청각장애 고객 전용. 한·영·중·일·베트남어 변호사 교대 대기.",
        },
        {
          title: "사톤 본사 직접 방문",
          body:
            "30분 이내에 완료 가능한 공증 사건은 당일 처리. 원본, 여권, 법인 건은 DBD 인증 등본을 지참하시기 바랍니다.",
        },
      ],
      factsHeading: "연락 전 준비 사항",
      facts: [
        "공증 대상 원본 및 미리보기용 스캔/사진.",
        "서명자 여권(태국 국적은 신분증).",
        "접수 기관명과 국가 — 대사관, 법원, 부처, 대학, 은행.",
        "문서 목적 — 혼인신고, 취업허가, 입양, 입찰, 주식양도 등.",
        "마감일. 대사관 면접이나 접수 시점이 확정된 경우 함께 공유.",
        "선호 접수 장소 — 사톤 본사, 지점, 변호사 출장.",
        "제출 관할지 언어(인증 번역 필요 여부 판단용).",
        "법인 사건 — DBD 등본, 이사회 결의서, 주주 명부.",
      ],
      closing:
        "착수 전 반드시 서면 견적을 제시하고, 해당 공증 행위가 태국법상 가능한지도 서면으로 확인합니다. 태국 변호사 권한을 초과하는 선서가 요구되는 경우에는 사실 그대로 안내드리고, 적절한 주태국 대사관 공증 부서 또는 태국 법원으로 안내합니다.",
    },
    coverage: {
      kicker: "서비스 지역 · 태국 전역 77도 + 방콕 50구",
      heading: "방콕 사무소가 태국 전 도(道)를 커버하는 방법",
      intro:
        "「태국 전역 서비스」는 마케팅 문구가 아니라 운영 설계상의 과제입니다. 공증 변호사는 서명 시 물리적으로 입회해야 하며, 원본은 서명자, 사무소, 방콕 챙왓타나 도로의 태국 외무부 영사국, 그리고 접수 대사관 사이를 왕복해야 합니다. 당사는 이 과제를 세 가지로 해결했습니다: 방콕·치앙마이·파타야·푸껫·핫야이 상주 변호사, 48시간 이내 태국 어느 도(道)든 변호사를 파견하는 「모바일 공증」 제도, 그리고 보세 관리 체인을 갖춘 협력 특송망입니다.",
      intro2:
        "방콕 이외 76개 도(道) 거주 고객의 표준 절차는 다음과 같습니다: (1) LINE·카카오톡·Zoom을 통한 사전 온라인 상담, (2) 위변조 방지 봉투를 이용한 방콕 원본 발송, (3) 변호사 공증 및 필요 시 외무부 인증, (4) 배송 추적이 되는 특송 반환. 물리적 입회가 필요한 사건—특히 중대한 금전적 결과를 수반하는 위임장—만 방문 또는 변호사 출장을 안내합니다.",
      cardsHeading: "지역별 운영 배치",
      cards: [
        {
          title: "방콕 광역권",
          body:
            "50개 구를 사톤 본사에서 직접 커버. 병원 침상 공증, 노년 고객 방문, 기밀성이 요구되는 법인 서명도 당일 대응 가능.",
        },
        {
          title: "중부 지역",
          body:
            "아유타야, 빠툼타니, 논타부리, 사뭇쁘라깐, 사뭇사콘, 사뭇송크람, 나콘빠톰, 수판부리, 싱부리는 2시간 거리 내 익영업일 대응.",
        },
        {
          title: "북부 지역",
          body:
            "치앙마이 지점이 치앙마이, 치앙라이, 람빵, 람푼, 난, 프래, 파야오, 매홍손을 담당. 은퇴 비자, 디지털 노마드, 국제 입양 사건이 주력.",
        },
        {
          title: "동부 해안 및 안다만",
          body:
            "동부 해안의 파타야(촌부리), 라용, 짠타부리, 뜨랏, 안다만의 푸껫, 끄라비, 팡응아, 라농. 부동산 매입, 혼인 등록, 외국인 상속 계획이 주요 업무.",
        },
        {
          title: "이산 및 남부 심부",
          body:
            "동북부의 나콘랏차시마, 콘깬, 우돈타니, 우본랏차타니, 부리람, 수린, 남부 심부의 핫야이, 송클라, 얄라, 빠따니, 나라티왓을 핫야이 지점과 출장 파견으로 커버.",
        },
      ],
      factsHeading: "지역 커버 핵심 데이터",
      facts: [
        "태국 77개 도(道) 전역 지원 — 원격 지역은 모바일 공증 파견.",
        "방콕 50개 구는 사톤 본사에서 직접, 당일 예약 가능.",
        "상주 거점 5곳: 방콕(사톤), 치앙마이, 파타야, 푸껫, 핫야이.",
        "협력 특송: Kerry Express(국내), DHL Express(국경 간 인증 문서).",
        "외무부 영사국 전담 러너가 매 영업일 상주.",
        "대사관 지구 사무실은 주태국 한국·미국·영국·중국 대사관 도보권.",
        "모든 77개 도(道)에서 화상 초기 상담 가능.",
        "모바일 공증은 서면 위임 후 48시간 이내 파견, 교통비는 선지급.",
      ],
      closing:
        "지방 사건으로 마감이 촉박한 경우, 도(道) 이름과 마감일을 알려주십시오. 1영업시간 이내에 특송+방콕 공증 또는 변호사 출장 중 최적안을 확인하고 두 가지 견적을 함께 제시하여, 예산과 통제 범위에 맞춰 선택하실 수 있도록 하겠습니다.",
    },
  },

  // ─────────────────────────── Tiếng Việt ───────────────────────────
  vi: {
    about: {
      kicker: "Giới thiệu văn phòng · Thành lập năm 2015",
      heading: "Vì sao Thai Notary Law trở thành lựa chọn ưu tiên cho hồ sơ xuyên biên giới",
      intro:
        "Thai Notary Law & Service Co., Ltd. được sáu luật sư có chứng chỉ công chứng viên (Notarial Services Attorney) của Hội Luật sư Thái Lan thành lập tại Bangkok năm 2015. Trước khi lập văn phòng, các đối tác sáng lập đã có hơn mười năm xử lý hồ sơ chứng thực cho đại sứ quán, tập đoàn đa quốc gia và các gia đình người nước ngoài đang sinh sống tại Thái Lan, do đó nắm rõ mọi cạm bẫy có thể xảy ra trong chuỗi ký – dịch – hợp pháp hóa Bộ Ngoại giao – hợp pháp hóa đại sứ quán. Đến nay, văn phòng xử lý hơn 4.800 bộ hồ sơ chứng thực xuyên biên giới mỗi năm, phục vụ khách hàng ở năm châu lục — trong đó cộng đồng người Việt tại Bangkok, Chonburi, Chiang Mai và Udon Thani chiếm tỷ trọng ngày càng lớn.",
      intro2:
        "Thế mạnh cốt lõi của chúng tôi là kiểm soát quy trình. Mọi hồ sơ công chứng, dịch có chứng thực và hợp pháp hóa Bộ Ngoại giao đều được ghi nhận trong hệ thống quản lý vụ việc, khách hàng theo dõi tiến độ theo thời gian thực. Do Thái Lan chưa gia nhập Công ước Hague Apostille tính đến tháng 7 năm 2026, mọi tài liệu sử dụng ngoài lãnh thổ phải do luật sư trực tiếp mang đến Cục Lãnh sự – Bộ Ngoại giao trên đường Chaeng Watthana Bangkok để chứng nhận, sau đó (nếu cần) tiếp tục hợp pháp hóa tại đại sứ quán quốc gia tiếp nhận. Việc luật sư đảm nhận toàn bộ chuỗi vật lý này chính là lý do các hãng luật di trú tại TP. Hồ Chí Minh và Hà Nội thường xuyên giới thiệu khách hàng có phần Thái Lan cho chúng tôi.",
      cardsHeading: "Năm khác biệt tạo nên uy tín",
      cards: [
        {
          title: "Chỉ luật sư có chứng chỉ công chứng phụ trách",
          body:
            "Cả sáu đối tác đều nắm giữ chứng chỉ Notarial Services Attorney do Hội Luật sư Thái Lan cấp theo Luật Luật sư B.E. 2528, gia hạn theo chu kỳ hai năm. Đây là chứng chỉ duy nhất pháp luật Thái Lan công nhận để công chứng chữ ký, tuyên thệ, sao y và dịch thuật.",
        },
        {
          title: "Bảy ngôn ngữ làm việc — dịch vụ trọn gói",
          body:
            "Đội dịch nội bộ đã tuyên thệ với Bộ Ngoại giao Thái Lan, làm việc thành thạo tiếng Việt, Thái, Anh, Trung (giản – phồn), Nhật, Hàn, Indonesia. Các ngôn ngữ Đức, Pháp, Nga, Ả Rập được đối tác dịch hỗ trợ. Điều này gần như loại bỏ nhu cầu thuê ngoài, giúp kiểm soát chất lượng và thời gian.",
        },
        {
          title: "Kênh hẹn cố định với đại sứ quán",
          body:
            "Chúng tôi duy trì lịch hẹn thường trực với Đại sứ quán Việt Nam tại Bangkok cũng như các đại sứ quán Mỹ, Anh, Úc, Canada, Đức, Pháp, Nhật, Hàn, Trung, Indonesia — có thể rút thời gian hợp pháp hóa đại sứ quán từ 3 tuần xuống còn 5 ngày làm việc.",
        },
        {
          title: "Lưu trữ chuẩn tuân thủ",
          body:
            "Mỗi hồ sơ công chứng được lưu trong kho niêm phong trong 10 năm, có nhật ký kiểm toán truy vết, đáp ứng yêu cầu thẩm định phòng chống gian lận của OECD và các câu hỏi xác minh từ DIFC / ADGM.",
        },
        {
          title: "Phí cố định, báo giá minh bạch",
          body:
            "Bảng giá công khai áp dụng cho phần lớn hồ sơ tiêu chuẩn. Với các trường hợp cần báo giá riêng — cấu trúc tín thác, con nuôi quốc tế, tái cấu trúc tập đoàn — chúng tôi luôn phát hành thư uỷ quyền bằng văn bản trước khi thao tác vào hồ sơ.",
        },
      ],
      factsHeading: "Số liệu cốt lõi",
      facts: [
        "Pháp nhân — Thai Notary Law & Service Co., Ltd., đăng ký DBD 0105558XXXXXX (2015).",
        "Trụ sở — Sathorn, Bangkok; chi nhánh Chiang Mai, Pattaya, Phuket, Hat Yai.",
        "Đội ngũ — 6 luật sư đối tác có chứng chỉ công chứng + 12 luật sư cộng sự.",
        "Dịch giả — 9 dịch giả tuyên thệ với Bộ Ngoại giao, phủ 7 ngôn ngữ làm việc.",
        "Khối lượng năm — 4.800+ bản công chứng, 3.200+ bản dịch chứng thực, 1.500+ hồ sơ hợp pháp hóa Bộ Ngoại giao.",
        "Công chứng chuẩn hoàn thành trong 4 giờ làm việc, dịch vụ hỏa tốc trong ngày.",
        "Phủ toàn bộ 77 tỉnh Thái Lan, có thể cử luật sư đến tận nơi.",
        "Cấu trúc khách hàng — đại sứ quán giới thiệu, doanh nghiệp, cá nhân và hợp tác cùng các hãng luật.",
      ],
      closing:
        "Dù là bản dịch giấy khai sinh cho visa hôn nhân, biên bản họp Hội đồng quản trị của công ty con Thái Lan gửi trụ sở tại Hà Nội, hay hồ sơ nhận con nuôi để nộp Toà án Nhân dân TP. HCM, luật sư của chúng tôi theo sát vụ việc từ khâu tiếp nhận đến khâu nhận hồ sơ ở đại sứ quán. Trách nhiệm xuyên suốt ấy là lý do khách hàng cũ liên tục giới thiệu khách hàng mới cho chúng tôi.",
    },
    contact: {
      kicker: "Liên hệ · Giờ Bangkok UTC+7",
      heading: "Cách nhanh nhất để chốt lịch hẹn với công chứng viên Bangkok",
      intro:
        "Trong giờ làm việc Bangkok (Thứ Hai – Thứ Bảy, 09:00 – 18:00 ICT), hầu hết yêu cầu được luật sư có chứng chỉ phản hồi lần đầu trong vòng 15 phút. Nếu vụ việc thực sự khẩn cấp — chuyến bay trong 24 giờ, phỏng vấn đại sứ quán ngày mai, chuyển khoản phải hoàn tất trong hôm nay — hãy ghi rõ chữ «KHẨN» ở dòng đầu tin nhắn để được xử lý ưu tiên. Với sự cố đại sứ quán ngoài giờ, chúng tôi bố trí luật sư trực đến 22:00 ICT.",
      intro2:
        "Cách liên hệ đầu tiên hiệu quả nhất là một tin nhắn LINE hoặc Zalo/WhatsApp gồm ba yếu tố: quốc gia tiếp nhận, loại tài liệu (giấy khai sinh, uỷ quyền, nghị quyết công ty…), và hạn chót. Chỉ cần ba dữ liệu này, chúng tôi có thể báo phí, xác định ngôn ngữ cần dịch chứng thực và đặt luật sư phù hợp — thường xong trước khi bạn uống hết ly cà phê. Câu hỏi chi tiết sẽ được gửi sau khi lịch hẹn được xác nhận, tránh làm mất thời gian của bạn.",
      cardsHeading: "Chọn kênh liên hệ phù hợp",
      cards: [
        {
          title: "LINE Official (khuyên dùng cho khách tại Thái Lan)",
          body:
            "LINE @thainotarylaw được luật sư thay ca theo dõi từ 08:00 – 22:00 ICT, hỗ trợ xem trước tài liệu. Chỉ cần gửi ảnh chụp điện thoại là nhận được báo giá chính xác cùng khuyến nghị ngôn ngữ.",
        },
        {
          title: "Zalo / WhatsApp Business (khuyên dùng cho khách ở nước ngoài)",
          body:
            "Với khách hàng ở Việt Nam, châu Âu, châu Mỹ, vùng Vịnh, đây là kênh xuyên múi giờ nhanh nhất. Có thể ghi âm mô tả; chúng tôi chuyển sang văn bản và phân luật sư phụ trách.",
        },
        {
          title: "Email — case@thainotarylaw.com",
          body:
            "Ưu tiên cho pháp chế doanh nghiệp, cố vấn nội bộ, luật sư hợp tác. Phản hồi trong 1 giờ làm việc, thư uỷ quyền chính thức phát hành trong 4 giờ.",
        },
        {
          title: "Điện thoại — +66-2-XXX-XXXX",
          body:
            "Dành cho tình huống khẩn cấp liên quan đại sứ quán và khách hàng có khiếm thính. Có luật sư nói tiếng Việt, Anh, Trung, Nhật, Hàn thay ca.",
        },
        {
          title: "Đến trực tiếp trụ sở Sathorn",
          body:
            "Các vụ việc công chứng dưới 30 phút có thể xử lý trong ngày. Mang theo bản gốc, hộ chiếu; đối với vụ việc doanh nghiệp, bổ sung trích lục DBD có chứng thực.",
        },
      ],
      factsHeading: "Cần chuẩn bị trước khi liên hệ",
      facts: [
        "Bản gốc cần công chứng và bản scan/ảnh để xem trước.",
        "Hộ chiếu của người ký (người Thái mang CCCD).",
        "Tên và quốc gia của cơ quan tiếp nhận — đại sứ quán, toà án, bộ, đại học, ngân hàng.",
        "Mục đích — đăng ký kết hôn, giấy phép lao động, nhận con nuôi, đấu thầu, chuyển nhượng cổ phần…",
        "Hạn chót. Nếu phụ thuộc vào lịch đại sứ quán, chia sẻ khung phỏng vấn/nộp.",
        "Địa điểm ưu tiên — trụ sở Sathorn, chi nhánh, hoặc luật sư đến tận nơi.",
        "Ngôn ngữ của khu vực tiếp nhận (để xác định có cần dịch chứng thực).",
        "Vụ việc doanh nghiệp — trích lục DBD, nghị quyết HĐQT, sổ cổ đông.",
      ],
      closing:
        "Chúng tôi luôn phát hành báo giá bằng văn bản trước khi bắt đầu công việc, và luật sư sẽ xác nhận bằng văn bản liệu pháp luật Thái Lan có cho phép hành vi công chứng bạn yêu cầu hay không. Nếu vượt quá thẩm quyền công chứng viên Thái Lan (ví dụ một số mẫu nước ngoài yêu cầu tuyên thệ đặc biệt), chúng tôi sẽ nói rõ và chuyển bạn đến bộ phận công chứng đại sứ quán hoặc toà án Thái phù hợp.",
    },
    coverage: {
      kicker: "Phạm vi phục vụ · Toàn Thái Lan + 50 quận Bangkok",
      heading: "Cách văn phòng Bangkok phục vụ trọn vẹn 77 tỉnh Thái Lan",
      intro:
        "«Phục vụ toàn quốc» không phải khẩu hiệu tiếp thị, mà là một bài toán vận hành: công chứng viên phải có mặt tại chỗ khi khách ký; bản gốc phải đi lại giữa người ký, văn phòng, Cục Lãnh sự – Bộ Ngoại giao Thái Lan trên đường Chaeng Watthana Bangkok, và đại sứ quán quốc gia tiếp nhận. Chúng tôi giải bài toán này theo ba hướng: luật sư thường trú tại Bangkok, Chiang Mai, Pattaya, Phuket, Hat Yai; chương trình «công chứng lưu động» cử luật sư đến bất kỳ tỉnh nào trong 48 giờ; và mạng lưới chuyển phát bảo hiểm có chuỗi bảo quản khép kín.",
      intro2:
        "Với khách hàng ở 76 tỉnh ngoài Bangkok, quy trình chuẩn là: (1) tư vấn trực tuyến sơ bộ qua LINE/Zalo/Zoom, (2) chuyển bản gốc về Bangkok trong túi niêm phong chống mở, (3) luật sư công chứng và (nếu cần) hợp pháp hóa Bộ Ngoại giao, (4) chuyển phát hoàn trả có mã theo dõi. Chỉ những vụ việc bắt buộc ký trực tiếp trước mặt luật sư — thường là uỷ quyền tài chính lớn — mới cần khách đến hoặc luật sư đến tận nơi.",
      cardsHeading: "Vận hành theo khu vực",
      cards: [
        {
          title: "Vùng đô thị Bangkok",
          body:
            "50 quận do trụ sở Sathorn phụ trách trực tiếp. Luật sư có thể đến giường bệnh, đến nhà khách hàng cao tuổi, hoặc đến trụ sở doanh nghiệp cho các vụ ký cần bảo mật, ngay trong ngày.",
        },
        {
          title: "Trung Thái",
          body:
            "Ayutthaya, Pathum Thani, Nonthaburi, Samut Prakan, Samut Sakhon, Samut Songkhram, Nakhon Pathom, Suphan Buri, Sing Buri nằm trong bán kính 2 giờ, có thể xử lý vào ngày làm việc kế tiếp.",
        },
        {
          title: "Bắc Thái",
          body:
            "Chi nhánh Chiang Mai phụ trách Chiang Mai, Chiang Rai, Lampang, Lamphun, Nan, Phrae, Phayao, Mae Hong Son — nhiều nhất là visa nghỉ hưu, digital nomad, con nuôi quốc tế.",
        },
        {
          title: "Bờ biển Đông & Andaman",
          body:
            "Bờ Đông có Pattaya (Chonburi), Rayong, Chanthaburi, Trat; bờ Andaman có Phuket, Krabi, Phang-nga, Ranong. Vụ việc chủ yếu: mua bất động sản, đăng ký kết hôn, kế hoạch di sản của người nước ngoài.",
        },
        {
          title: "Isan & Nam sâu",
          body:
            "Đông Bắc: Nakhon Ratchasima, Khon Kaen, Udon Thani, Ubon Ratchathani, Buriram, Surin; Nam sâu: Hat Yai, Songkhla, Yala, Pattani, Narathiwat — do chi nhánh Hat Yai và cử luật sư lưu động phối hợp.",
        },
      ],
      factsHeading: "Số liệu phạm vi phục vụ",
      facts: [
        "Phục vụ toàn bộ 77 tỉnh — vùng sâu vùng xa qua luật sư lưu động.",
        "50 quận Bangkok do trụ sở Sathorn phụ trách, có lịch trong ngày.",
        "Năm chi nhánh: Bangkok (Sathorn), Chiang Mai, Pattaya, Phuket, Hat Yai.",
        "Đối tác chuyển phát: Kerry Express (nội địa), DHL Express (quốc tế hồ sơ đã hợp pháp hóa).",
        "Có nhân sự chuyên trách chạy hồ sơ đến Cục Lãnh sự mỗi ngày làm việc.",
        "Văn phòng khu đại sứ quán nằm trong khoảng đi bộ đến đại sứ quán Mỹ, Anh, Nhật, Trung.",
        "Hỗ trợ tư vấn video ban đầu tại tất cả 77 tỉnh.",
        "Công chứng lưu động triển khai trong 48 giờ sau khi có thư uỷ quyền, chi phí đi lại thu trước.",
      ],
      closing:
        "Nếu vụ việc ở tỉnh xa và gấp, hãy cho chúng tôi tên tỉnh và hạn chót. Trong 1 giờ làm việc, chúng tôi sẽ xác nhận nên áp dụng chuyển phát + công chứng tại Bangkok hay điều luật sư đến tận nơi, kèm hai báo giá song song để bạn chọn dựa trên chi phí và mức độ kiểm soát mong muốn.",
    },
  },

  // ─────────────────────────── Bahasa Indonesia ───────────────────────────
  id: {
    about: {
      kicker: "Profil firma · Berdiri sejak 2015",
      heading: "Mengapa Thai Notary Law menjadi mitra pilihan untuk dokumen lintas negara",
      intro:
        "Thai Notary Law & Service Co., Ltd. didirikan di Bangkok pada tahun 2015 oleh enam pengacara pemegang komisi Notarial Services Attorney dari Lawyers Council of Thailand. Sebelum mendirikan firma, para partner sudah lebih dari satu dekade menangani legalisasi dokumen untuk kedutaan, perusahaan multinasional, dan keluarga ekspatriat di Thailand — sehingga sangat memahami jebakan yang mungkin muncul di setiap tahap: penandatanganan, penerjemahan tersumpah, legalisasi Kementerian Luar Negeri, hingga legalisasi kedutaan negara tujuan. Saat ini firma menangani lebih dari 4.800 dokumen tersertifikasi lintas negara setiap tahun, dengan klien dari lima benua — termasuk komunitas Indonesia yang berkembang pesat di Bangkok, Chonburi, dan Chiang Mai.",
      intro2:
        "Keunggulan utama kami adalah kendali proses. Setiap notarisasi, terjemahan bersertifikat, dan legalisasi Kementerian Luar Negeri tercatat dalam sistem manajemen kasus dengan pembaruan status real-time. Karena Thailand belum tergabung dalam Konvensi Apostille Den Haag (per Juli 2026), setiap dokumen yang akan digunakan di luar negeri harus dibawa langsung oleh pengacara ke Direktorat Konsuler Kementerian Luar Negeri di Jalan Chaeng Watthana Bangkok, lalu — bila diperlukan — dilanjutkan ke kedutaan negara tujuan. Rantai pengawasan fisik yang kami jalankan inilah yang membuat law firm imigrasi di Jakarta dan Surabaya mempercayakan bagian Thailand dari kasus mereka kepada kami.",
      cardsHeading: "Lima pembeda utama praktik kami",
      cards: [
        {
          title: "Hanya pengacara bersertifikat notaris",
          body:
            "Keenam partner memegang lisensi Notarial Services Attorney (NSA) dari Lawyers Council of Thailand sesuai UU Advokat B.E. 2528, diperbarui setiap dua tahun. Lisensi inilah satu-satunya kredensial yang diakui hukum Thailand untuk notarisasi tanda tangan, sumpah, salinan, dan terjemahan.",
        },
        {
          title: "Tujuh bahasa kerja — layanan terpadu",
          body:
            "Penerjemah in-house telah bersumpah di Kementerian Luar Negeri Thailand untuk bahasa Indonesia, Thai, Inggris, Mandarin (aksara sederhana dan tradisional), Jepang, Korea, dan Vietnam. Bahasa Jerman, Prancis, Rusia, Arab tersedia melalui jaringan mitra. Kombinasi ini mengurangi kebutuhan outsourcing sehingga kualitas dan tenggat waktu tetap terjaga.",
        },
        {
          title: "Slot janji tetap dengan kedutaan",
          body:
            "Kami memiliki jalur janji temu tetap dengan Kedutaan Besar Republik Indonesia di Bangkok, serta kedutaan AS, Inggris, Australia, Kanada, Jerman, Prancis, Jepang, Korea, Tiongkok, dan Vietnam — mampu memangkas waktu legalisasi kedutaan yang biasanya tiga minggu menjadi lima hari kerja.",
        },
        {
          title: "Arsip kelas kepatuhan",
          body:
            "Setiap dokumen tersertifikasi disimpan sepuluh tahun di arsip tersegel dengan jejak audit terlacak, memenuhi persyaratan uji tuntas anti-penipuan OECD serta permintaan verifikasi dari DIFC dan ADGM di Timur Tengah.",
        },
        {
          title: "Tarif tetap dan transparan",
          body:
            "Daftar tarif publik mencakup sebagian besar kasus standar. Untuk pekerjaan yang benar-benar memerlukan penawaran khusus — struktur trust, adopsi lintas yurisdiksi, restrukturisasi korporasi — surat penunjukan ditandatangani lebih dulu sebelum kami menyentuh berkas.",
        },
      ],
      factsHeading: "Data ringkas firma",
      facts: [
        "Badan hukum — Thai Notary Law & Service Co., Ltd., registrasi DBD 0105558XXXXXX (2015).",
        "Kantor pusat — Sathorn, Bangkok; cabang di Chiang Mai, Pattaya, Phuket, Hat Yai.",
        "Tim advokat — 6 partner NSA + 12 pengacara asosiat.",
        "Penerjemah in-house — 9 penerjemah bersumpah untuk tujuh bahasa kerja.",
        "Volume tahunan — 4.800+ dokumen tersertifikasi, 3.200+ terjemahan bersertifikat, 1.500+ legalisasi MFA.",
        "Notarisasi standar selesai 4 jam kerja, layanan super-kilat tersedia atas permintaan.",
        "Menjangkau seluruh 77 provinsi Thailand, pengacara dapat datang ke lokasi.",
        "Struktur klien — rujukan kedutaan, korporasi, individu, dan kolaborasi dengan firma hukum.",
      ],
      closing:
        "Baik itu terjemahan akta lahir untuk mengurus visa pasangan, notulen RUPS anak perusahaan Thailand untuk kantor pusat di Jakarta, atau dokumen adopsi internasional untuk Pengadilan Agama, pengacara kami mengawal perkara dari intake hingga pengambilan berkas di kedutaan. Tanggung jawab ujung-ke-ujung inilah alasan mengapa klien lama secara konsisten merujuk kasus berikutnya kepada kami.",
    },
    contact: {
      kicker: "Cara menghubungi · Waktu Bangkok UTC+7",
      heading: "Cara tercepat mendapatkan janji temu notaris Bangkok",
      intro:
        "Selama jam kerja Bangkok (Senin – Sabtu, 09:00 – 18:00 ICT), sebagian besar pertanyaan direspons oleh pengacara bersertifikat dalam 15 menit. Bila perkara benar-benar mendesak — penerbangan dalam 24 jam, wawancara kedutaan besok, transfer yang wajib beres hari ini — cantumkan kata «URGENT» pada baris pertama pesan Anda supaya langsung diprioritaskan. Untuk keadaan darurat terkait kedutaan di luar jam kerja, kami menyediakan pengacara siaga hingga pukul 22:00 ICT.",
      intro2:
        "Cara paling efisien untuk memulai adalah pesan LINE atau WhatsApp berisi tiga elemen: negara tujuan, jenis dokumen (akta lahir, surat kuasa, keputusan direksi, dll.), dan tenggat waktu. Berbekal tiga informasi ini kami dapat memberikan penawaran, menentukan kebutuhan terjemahan bersertifikat, serta mengunci pengacara yang tepat — biasanya sebelum Anda menghabiskan kopi berikutnya. Formulir rinci hanya dikirim setelah janji temu dikonfirmasi.",
      cardsHeading: "Memilih saluran kontak yang tepat",
      cards: [
        {
          title: "LINE Official (disarankan untuk klien di Thailand)",
          body:
            "LINE @thainotarylaw dipantau bergantian oleh pengacara pukul 08:00 – 22:00 ICT dan mendukung pratinjau dokumen. Foto dokumen dari ponsel sudah cukup untuk mendapatkan penawaran dan rekomendasi bahasa yang akurat.",
        },
        {
          title: "WhatsApp Business (disarankan untuk klien luar negeri)",
          body:
            "Bagi klien di Indonesia, Eropa, Amerika, dan Teluk, WhatsApp adalah saluran lintas zona waktu tercepat. Pesan suara pun kami terima; akan kami transkrip dan arahkan ke pengacara yang sesuai.",
        },
        {
          title: "Email — case@thainotarylaw.com",
          body:
            "Cocok untuk sekretaris perusahaan, in-house counsel, dan law firm mitra yang memerlukan jejak tertulis. Respons awal dalam 1 jam kerja, surat penunjukan resmi dalam 4 jam.",
        },
        {
          title: "Telepon — +66-2-XXX-XXXX",
          body:
            "Diperuntukkan bagi eskalasi darurat kedutaan dan klien tuna rungu. Pengacara berbahasa Indonesia, Inggris, Mandarin, Jepang, dan Korea tersedia bergiliran.",
        },
        {
          title: "Kunjungan langsung ke kantor pusat Sathorn",
          body:
            "Kasus notaris di bawah 30 menit dapat dituntaskan hari itu juga. Bawa dokumen asli, paspor, dan untuk kasus perusahaan lampirkan salinan DBD tersertifikasi.",
        },
      ],
      factsHeading: "Persiapan sebelum menghubungi",
      facts: [
        "Dokumen asli yang akan dinotariskan plus salinan digital untuk pratinjau.",
        "Paspor penandatangan (KTP untuk WNI/WN Thailand).",
        "Nama dan negara instansi penerima — kedutaan, pengadilan, kementerian, universitas, bank.",
        "Tujuan — pencatatan nikah, izin kerja, adopsi, tender, alih saham, dll.",
        "Tenggat waktu; bila bergantung pada kedutaan, cantumkan slot wawancara/pengajuan.",
        "Lokasi janji temu preferensi — kantor pusat Sathorn, cabang, atau kunjungan pengacara.",
        "Bahasa yurisdiksi penerima untuk menilai kebutuhan terjemahan bersertifikat.",
        "Dokumen perusahaan — salinan DBD, keputusan direksi, buku pemegang saham.",
      ],
      closing:
        "Kami selalu menerbitkan penawaran tertulis sebelum pekerjaan dimulai, dan pengacara akan mengonfirmasi secara tertulis apakah hukum Thailand mengizinkan tindakan notarisasi yang Anda minta. Jika suatu sumpah berada di luar kewenangan pengacara Thailand, kami akan menyampaikannya secara jujur dan merujuk Anda ke bagian notaris kedutaan yang tepat atau pengadilan Thailand.",
    },
    coverage: {
      kicker: "Jangkauan layanan · Seluruh Thailand + 50 distrik Bangkok",
      heading: "Cara firma yang berbasis di Bangkok melayani seluruh 77 provinsi Thailand",
      intro:
        "«Melayani seluruh Thailand» bukan sekadar klaim pemasaran, melainkan sebuah persoalan operasional: pengacara notaris wajib hadir fisik saat tanda tangan dibuat, sementara dokumen asli harus berpindah antara penandatangan, kantor kami, Direktorat Konsuler Kementerian Luar Negeri di Jalan Chaeng Watthana Bangkok, dan kedutaan negara tujuan. Kami menyelesaikan masalah operasional ini melalui tiga jalur: pengacara yang berdomisili di Bangkok, Chiang Mai, Pattaya, Phuket, dan Hat Yai; program «notaris keliling» yang dapat mengirim pengacara ke provinsi mana pun dalam 48 jam; serta jaringan kurir tersegel yang menjaga rantai pengawasan.",
      intro2:
        "Untuk klien di 76 provinsi di luar Bangkok, alur kerja standarnya adalah: (1) konsultasi awal via LINE, WhatsApp, atau Zoom; (2) pengiriman dokumen asli ke Bangkok dalam amplop anti-manipulasi; (3) notarisasi oleh pengacara dan, jika perlu, legalisasi Kementerian Luar Negeri; (4) pengembalian melalui kurir bernomor lacak. Kehadiran tatap muka hanya diminta untuk tanda tangan yang harus dieksekusi langsung di hadapan pengacara — umumnya surat kuasa dengan konsekuensi finansial besar.",
      cardsHeading: "Operasional per wilayah",
      cards: [
        {
          title: "Wilayah Metropolitan Bangkok",
          body:
            "50 distrik dilayani langsung dari kantor pusat Sathorn. Pengacara dapat mendatangi tepi ranjang rumah sakit, kediaman klien lansia, hingga kantor korporasi untuk penandatanganan yang memerlukan kerahasiaan — semuanya di hari yang sama.",
        },
        {
          title: "Thailand Tengah",
          body:
            "Ayutthaya, Pathum Thani, Nonthaburi, Samut Prakan, Samut Sakhon, Samut Songkhram, Nakhon Pathom, Suphan Buri, dan Sing Buri berada dalam radius dua jam berkendara dan dilayani pada hari kerja berikutnya.",
        },
        {
          title: "Thailand Utara",
          body:
            "Cabang Chiang Mai menangani Chiang Mai, Chiang Rai, Lampang, Lamphun, Nan, Phrae, Phayao, dan Mae Hong Son. Populer di kalangan pemohon visa pensiun, digital nomad, dan keluarga adopsi internasional.",
        },
        {
          title: "Pantai Timur & Andaman",
          body:
            "Pantai Timur meliputi Pattaya (Chonburi), Rayong, Chanthaburi, Trat; pesisir Andaman meliputi Phuket, Krabi, Phang-nga, Ranong. Umumnya digunakan untuk pembelian properti, pencatatan pernikahan, dan perencanaan warisan ekspatriat.",
        },
        {
          title: "Isan & Selatan dalam",
          body:
            "Timur Laut: Nakhon Ratchasima, Khon Kaen, Udon Thani, Ubon Ratchathani, Buriram, Surin; Selatan dalam: Hat Yai, Songkhla, Yala, Pattani, Narathiwat — dilayani oleh cabang Hat Yai dan penugasan keliling.",
        },
      ],
      factsHeading: "Fakta jangkauan",
      facts: [
        "Melayani seluruh 77 provinsi Thailand — provinsi jauh melalui notaris keliling.",
        "50 distrik Bangkok dilayani langsung dari kantor pusat Sathorn dengan ketersediaan hari yang sama.",
        "Lima cabang: Bangkok (Sathorn), Chiang Mai, Pattaya, Phuket, Hat Yai.",
        "Mitra kurir: Kerry Express (domestik), DHL Express (dokumen legalisasi lintas negara).",
        "Kurir khusus ke Direktorat Konsuler Kementerian Luar Negeri setiap hari kerja.",
        "Kantor di kawasan kedutaan berjarak berjalan kaki ke KBRI, kedutaan AS, Inggris, dan Tiongkok.",
        "Konsultasi awal berbasis video tersedia di seluruh 77 provinsi.",
        "Notaris keliling dikerahkan dalam 48 jam setelah penunjukan tertulis, biaya perjalanan dibayar dimuka.",
      ],
      closing:
        "Bila perkara Anda berada di luar Bangkok dan waktu terbatas, kirim nama provinsi beserta tenggatnya. Dalam 1 jam kerja kami akan mengonfirmasi apakah alur terbaik adalah kurir + notarisasi di Bangkok atau pengiriman pengacara ke lokasi, sekaligus menyampaikan dua penawaran agar Anda dapat memilih berdasarkan biaya dan tingkat kendali yang diinginkan.",
    },
  },
};
